/**
 * Collection facets — clean vanilla rebuild of the live theme's Dawn
 * facets.js (+ the show-more.js / menu-drawer bits it depends on).
 *
 * Behavior contract (matches live):
 * - Any facet checkbox / sort select / price input change triggers a
 *   debounced (500ms) fetch of the current path with
 *   `?section_id=<id>&<params>` (Section Rendering API, responses cached),
 *   then swaps #ProductGridContainer, the facet groups (.js-filter), the
 *   active pill rows and the product counts, and pushes the URL.
 * - <facet-remove> pill links apply their href's query via AJAX.
 * - popstate re-renders the stored params (back/forward support).
 * - Without JS everything degrades to plain GET form submits / links.
 */

const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

const sectionCache = new Map();

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 500);
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
      });
    }
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static getSectionId() {
    const container = document.getElementById('ProductGridContainer');
    return container ? container.dataset.id : null;
  }

  static toggleLoading(loading) {
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !loading));
    const grid = document.querySelector('#ProductGridContainer .collection');
    if (grid) grid.classList.toggle('loading', loading);
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.toggle('loading', loading);
    });
    document
      .querySelectorAll('.js-facet-remove')
      .forEach((el) => el.classList.toggle('disabled', loading));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const sectionId = FacetFiltersForm.getSectionId();
    if (sectionId === null) return;
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.toggleLoading(true);

    const url = `${window.location.pathname}?section_id=${sectionId}${searchParams ? `&${searchParams}` : ''}`;
    const cached = sectionCache.get(url);
    const render = (html) => {
      FacetFiltersForm.renderResult(html, event);
      FacetFiltersForm.toggleLoading(false);
    };

    if (cached) {
      render(cached);
    } else {
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          sectionCache.set(url, responseText);
          render(responseText);
        })
        .catch(() => {
          FacetFiltersForm.toggleLoading(false);
        });
    }

    if (updateURLHash) {
      history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`
      );
    }
  }

  static renderResult(responseText, event) {
    const parsed = new DOMParser().parseFromString(responseText, 'text/html');

    // 1. Product grid + pagination
    const newGrid = parsed.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');
    if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;

    // 2. Facet groups (keep the group the user interacted with intact
    //    except for its values/summary so its open state + focus survive)
    const interacted = event ? event.target.closest('.js-filter') : null;
    const newFilters = parsed.querySelectorAll('.js-filter');
    const currentFilters = Array.from(document.querySelectorAll('.js-filter'));

    currentFilters.forEach((current) => {
      if (!Array.from(newFilters).some((el) => el.id === current.id)) current.remove();
    });

    newFilters.forEach((newFilter) => {
      const current = document.getElementById(newFilter.id);
      if (!current) return;
      if (interacted && current.id === interacted.id) {
        FacetFiltersForm.renderSelectedCounts(newFilter, current);
      } else {
        current.innerHTML = newFilter.innerHTML;
      }
    });

    // 3. Active pill rows
    ['.active-facets-desktop', '.active-facets-mobile'].forEach((selector) => {
      const source = parsed.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    // 4. Product counts (desktop sort bar + mobile)
    const newCount = parsed.getElementById('ProductCount');
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el && newCount) el.innerHTML = newCount.innerHTML;
    });
    const newMobileCount = parsed.querySelector('.mobile-facets__count');
    const mobileCount = document.querySelector('.mobile-facets__count');
    if (newMobileCount && mobileCount) mobileCount.innerHTML = newMobileCount.innerHTML;
  }

  // Swap value list + summary of the interacted group, preserving an
  // expanded "show more" state (Dawn renderCounts contract).
  static renderSelectedCounts(source, target) {
    const sourceSummary = source.querySelector('.facets__summary');
    const targetSummary = target.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const sourceWrap = source.querySelector('.facets-wrap');
    const targetWrap = target.querySelector('.facets-wrap');
    if (sourceWrap && targetWrap) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrap
          .querySelectorAll('.facets__item.hidden')
          .forEach((item) => item.classList.remove('hidden'));
        const sourceToggle = sourceWrap.querySelector('show-more-button');
        if (sourceToggle) {
          sourceToggle.querySelector('.label-show-more').classList.add('hidden');
          sourceToggle.querySelector('.label-show-less').classList.remove('hidden');
        }
      }
      targetWrap.outerHTML = sourceWrap.outerHTML;
    }

    const sourceList = source.querySelector('.mobile-facets__list');
    const targetList = target.querySelector('.mobile-facets__list');
    if (sourceList && targetList) targetList.outerHTML = sourceList.outerHTML;
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitHandler(event) {
    if (event) event.preventDefault();
    const sourceForm = event.target.closest('form');
    const isMobile = sourceForm && sourceForm.id === 'FacetFiltersFormMobile';
    const params = [];

    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      if (isMobile ? form.id === 'FacetFiltersFormMobile' : form.id !== 'FacetFiltersFormMobile') {
        const query = this.createSearchParams(form);
        if (query) params.push(query);
      }
    });

    FacetFiltersForm.renderPage(params.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href;
    const searchParams = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(searchParams, null);
  }
}

FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((input) =>
      input.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', (event) => {
      this.expandShowMore(event);
      const nowVisible = this.closest('.parent-display')?.querySelector(
        '.show-more-item:not(.hidden) input'
      );
      if (nowVisible) nowVisible.focus();
    });
  }

  expandShowMore(event) {
    const parent =
      event.target.closest('.parent-display') || event.target.closest('.facets-wrap');
    this.querySelectorAll('.label-text').forEach((el) => el.classList.toggle('hidden'));
    if (!parent) return;
    parent.querySelectorAll('.show-more-item').forEach((item) => item.classList.toggle('hidden'));
  }
}

customElements.define('show-more-button', ShowMoreButton);

/** Minimal mobile drawer: <details> based, with close buttons + Escape. */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.details.addEventListener('toggle', () => {
      document.body.classList.toggle('facets-drawer-open', this.details.open);
    });

    this.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    });
  }

  close() {
    if (this.details) this.details.open = false;
  }
}

if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
